import { XIcon } from 'lucide-react'

import { cn } from '@/lib/utils'

export type FormMessageType =
  | { success: string | null | undefined }
  | { error: string | null | undefined }
  | { message: string | null | undefined }
  | string
  | null

const VARIANT_STYLES = {
  success: {
    id: 'success-message',
    border: 'border-primary',
    bg: 'bg-primary/5',
    text: 'text-primary',
    role: 'status',
  },
  error: {
    id: 'error-message',
    border: 'border-destructive',
    bg: 'bg-destructive/5',
    text: 'text-destructive',
    role: 'alert',
  },
  message: {
    id: 'message',
    border: 'border-brand',
    bg: 'bg-brand/5',
    text: 'text-brand',
    role: 'status',
  },
} as const

function MessageRow({
  variant,
  text,
  onClear,
}: {
  variant: keyof typeof VARIANT_STYLES
  text: string
  onClear?: () => void
}) {
  const styles = VARIANT_STYLES[variant]

  return (
    <div
      id={styles.id}
      role={styles.role}
      aria-live={styles.role === 'alert' ? 'assertive' : 'polite'}
      className={cn(
        'corner-squircle flex items-center rounded-xl border-l-2 px-4 py-2 shadow-sm',
        styles.border,
        styles.bg,
        styles.text,
      )}
    >
      <span className="ml-2">{text}</span>
      {onClear ? (
        <button
          type="button"
          onClick={onClear}
          aria-label="Dismiss message"
          className="ml-auto"
        >
          <XIcon className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  )
}

export function FormMessage({
  message,
  onClear,
}: {
  message: FormMessageType
  onClear?: () => void
}) {
  if (!message) return null

  if (typeof message === 'string') {
    return (
      <div className="flex w-full max-w-md flex-col gap-2 text-sm">
        <MessageRow variant="error" text={message} onClear={onClear} />
      </div>
    )
  }

  const validMessage =
    ('success' in message && message.success) ||
    ('error' in message && message.error) ||
    ('message' in message && message.message)

  if (!validMessage) return null

  return (
    <div className="mt-2 flex w-full max-w-md flex-col gap-2 text-sm">
      {'success' in message && message.success ? (
        <MessageRow variant="success" text={message.success} onClear={onClear} />
      ) : null}
      {'error' in message && message.error ? (
        <MessageRow variant="error" text={message.error} onClear={onClear} />
      ) : null}
      {'message' in message && message.message ? (
        <MessageRow variant="message" text={message.message} onClear={onClear} />
      ) : null}
    </div>
  )
}
